// CampaignContext.tsx
// Provides campaign context for filtering data by campaign

import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from "react";
import { supabase } from "../providers/supabase/supabase";
import { useOrganization } from "./OrganizationContext";

export interface Campaign {
    id: number;
    name: string;
    slug: string;
    description?: string;
    color: string;
    icon: string;
    status: "active" | "paused" | "archived";
    contact_count: number;
    deal_count: number;
    total_deal_value: number;
    org_id: number;
}

interface CampaignContextValue {
    currentCampaign: Campaign | null;
    currentCampaignId: number | null;
    campaigns: Campaign[];
    switchCampaign: (campaignId: number | null) => void;
    isLoading: boolean;
    showAllCampaigns: boolean;
    setShowAllCampaigns: (show: boolean) => void;
    refreshCampaigns: () => Promise<void>;
}

const CampaignContext = createContext<CampaignContextValue | null>(null);

// Storage key prefix for persisting current campaign (per org)
const CAMPAIGN_KEY_PREFIX = "currentCampaignId_org_";
const SHOW_ALL_KEY_PREFIX = "showAllCampaigns_org_";

// Get current campaign ID from localStorage for a specific org
export function getCurrentCampaignId(orgId: number | null): number | null {
    if (!orgId) return null;
    const stored = localStorage.getItem(`${CAMPAIGN_KEY_PREFIX}${orgId}`);
    return stored ? Number(stored) : null;
}

// Set current campaign ID in localStorage
export function setCurrentCampaignId(orgId: number, campaignId: number | null): void {
    if (campaignId) {
        localStorage.setItem(`${CAMPAIGN_KEY_PREFIX}${orgId}`, String(campaignId));
    } else {
        localStorage.removeItem(`${CAMPAIGN_KEY_PREFIX}${orgId}`);
    }
}

// Get "show all" state from localStorage
export function getShowAllCampaigns(orgId: number | null): boolean {
    if (!orgId) return false;
    return localStorage.getItem(`${SHOW_ALL_KEY_PREFIX}${orgId}`) === "true";
}

// Set "show all" state in localStorage
export function setShowAllCampaignsStorage(orgId: number, showAll: boolean): void {
    localStorage.setItem(`${SHOW_ALL_KEY_PREFIX}${orgId}`, String(showAll));
}

export function CampaignProvider({ children }: { children: ReactNode }) {
    const { currentOrgId, isLoading: orgLoading } = useOrganization();

    const [currentCampaign, setCurrentCampaign] = useState<Campaign | null>(null);
    const [campaigns, setCampaigns] = useState<Campaign[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [showAllCampaigns, setShowAllCampaignsState] = useState(false);

    // Fetch campaigns when org changes
    useEffect(() => {
        if (orgLoading) return;
        if (!currentOrgId) {
            setCampaigns([]);
            setCurrentCampaign(null);
            setIsLoading(false);
            return;
        }

        fetchCampaigns();

        // Restore "show all" state
        setShowAllCampaignsState(getShowAllCampaigns(currentOrgId));
    }, [currentOrgId, orgLoading]);

    async function fetchCampaigns() {
        if (!currentOrgId) return;

        setIsLoading(true);
        try {
            const { data, error } = await supabase
                .from("campaigns")
                .select("*")
                .eq("org_id", currentOrgId)
                .in("status", ["active", "paused"])
                .order("name", { ascending: true });

            if (error) {
                console.error("Error fetching campaigns:", error);
                setCampaigns([]);
                setCurrentCampaign(null);
                return;
            }

            if (data && data.length > 0) {
                setCampaigns(data as Campaign[]);

                // Restore from localStorage or default to first campaign
                const savedCampaignId = getCurrentCampaignId(currentOrgId);
                const savedShowAll = getShowAllCampaigns(currentOrgId);

                if (savedShowAll) {
                    setCurrentCampaign(null);
                    setShowAllCampaignsState(true);
                } else {
                    let current = data.find((c: Campaign) => c.id === savedCampaignId);

                    // If no saved campaign or doesn't exist, use default campaign or first
                    if (!current) {
                        current = data.find((c: Campaign) => c.slug === "default") || data[0];
                    }

                    if (current) {
                        setCurrentCampaign(current as Campaign);
                        setCurrentCampaignId(currentOrgId, current.id);
                    }
                }
            } else {
                setCampaigns([]);
                setCurrentCampaign(null);
            }
        } catch (err) {
            console.error("Error in fetchCampaigns:", err);
        } finally {
            setIsLoading(false);
        }
    }

    const switchCampaign = useCallback((campaignId: number | null) => {
        if (!currentOrgId) return;

        if (campaignId === null) {
            // Switch to "all campaigns" mode
            setShowAllCampaignsState(true);
            setShowAllCampaignsStorage(currentOrgId, true);
            setCurrentCampaign(null);
            setCurrentCampaignId(currentOrgId, null);
        } else {
            const campaign = campaigns.find((c) => c.id === campaignId);
            if (campaign) {
                setCurrentCampaign(campaign);
                setShowAllCampaignsState(false);
                setShowAllCampaignsStorage(currentOrgId, false);
                setCurrentCampaignId(currentOrgId, campaignId);
            }
        }
        // Reload to refresh all data with new campaign context
        window.location.reload();
    }, [campaigns, currentOrgId]);

    const setShowAllCampaigns = useCallback((show: boolean) => {
        if (!currentOrgId) return;

        setShowAllCampaignsState(show);
        setShowAllCampaignsStorage(currentOrgId, show);

        if (show) {
            setCurrentCampaign(null);
            setCurrentCampaignId(currentOrgId, null);
        } else {
            // When turning off "show all", select default or first campaign
            const defaultCampaign = campaigns.find((c) => c.slug === "default") || campaigns[0];
            if (defaultCampaign) {
                setCurrentCampaign(defaultCampaign);
                setCurrentCampaignId(currentOrgId, defaultCampaign.id);
            }
        }
        window.location.reload();
    }, [campaigns, currentOrgId]);

    const refreshCampaigns = useCallback(async () => {
        await fetchCampaigns();
    }, [currentOrgId]);

    return (
        <CampaignContext.Provider
            value={{
                currentCampaign,
                currentCampaignId: currentCampaign?.id ?? null,
                campaigns,
                switchCampaign,
                isLoading,
                showAllCampaigns,
                setShowAllCampaigns,
                refreshCampaigns,
            }}
        >
            {children}
        </CampaignContext.Provider>
    );
}

export function useCampaign() {
    const context = useContext(CampaignContext);
    if (!context) {
        throw new Error("useCampaign must be used within a CampaignProvider");
    }
    return context;
}

// Optional hook that doesn't throw (for use outside provider)
export function useCampaignOptional() {
    return useContext(CampaignContext);
}
